import PropTypes from 'prop-types'
import { CheckCheck } from 'lucide-react'

import { AssigneeAvatarPicker } from '@components/assigned-user'
import { SubStatusPicker } from '@components/sub-status-picker'
import { Checkbox } from '@components/ui/checkbox'
import adasIntelligenceMark from '../navigation-bar/navigation-bar/Blue_trimmed_logo.svg'
import { LOST_SUB_STATUSES, StatusEnum } from '../../enums/enums'
import { currencyFormatter } from '../../utils/utils'
import { getWorkOrderStatusPill } from '../../utils/workOrderStatus'

const formatType = (t) => {
  if (!t) return null
  if (t === 'SERVICE_ONLY') return 'Service'
  if (t === 'STANDARD') return 'Standard'
  return t
}

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

export const QueueCard = ({ item, itemType, isSelected, onSelect, onClick, assigneeType }) => {
  const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
  // Work orders show calibration progress (CAR-3872); quotes and invoices keep their status.
  const statusPill =
    itemType === 'WO'
      ? getWorkOrderStatusPill(item)
      : {
          label: StatusEnum[item.status],
          pill: isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]',
          dot: isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'
        }
  const refPrefix = itemType === 'WO' ? 'WO #' : itemType === 'INV' ? 'Inv #' : 'Q #'
  const refLabel = `${refPrefix}${item.reference_number}`
  const isAi = item.source === 'ADAS_INTELLIGENCE' || item.ai_processed
  const vehicleName = [item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'
  const type = formatType(item.type)

  return (
    <div
      onClick={() => onClick?.(item)}
      className='cursor-pointer rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm transition-colors hover:bg-[#F8FAFC]'
    >
      {/* Top: checkbox + meta line + AI mark */}
      <div className='flex items-start gap-3'>
        <div onClick={(e) => e.stopPropagation()} className='pt-0.5'>
          <Checkbox checked={isSelected} onCheckedChange={() => onSelect?.(item)} aria-label={`Select ${refLabel}`} />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-x-1.5 text-[12px] text-[#6B7280]'>
              <span>{refLabel}</span>
              {item.repair_order_num && (
                <>
                  <span className='text-[#CBD5E1]'>|</span>
                  <span>RO #{item.repair_order_num}</span>
                </>
              )}
              {type && (
                <>
                  <span className='text-[#CBD5E1]'>|</span>
                  <span>{type}</span>
                </>
              )}
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              {isAi && (
                <img
                  src={adasIntelligenceMark}
                  alt='ADAS Intelligence'
                  title='Created from ADAS Intelligence'
                  className='h-3 w-auto shrink-0 object-contain'
                />
              )}
              <span
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${statusPill.pill}`}
              >
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${statusPill.dot}`} />
                {statusPill.label}
              </span>
            </div>
          </div>
          <div className='mt-1 flex flex-wrap items-center gap-2'>
            <span className='text-[16px] font-bold uppercase leading-tight tracking-tight text-[#0A1628]'>{vehicleName}</span>
            {!isOpen && item.quality_review?.is_completed && (
              <span
                title='Scorecard completed'
                aria-label='Scorecard completed'
                className='inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold uppercase text-[#047857]'
              >
                <CheckCheck className='h-3 w-3' />
                Scorecard
              </span>
            )}
          </div>
          {item.client?.name && (
            <div className='mt-0.5 text-[13px] text-[#6B7280]'>
              <span>{item.client.name}</span>
              {item.insurance_company?.name && (
                <>
                  <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                  <span>{item.insurance_company.name}</span>
                </>
              )}
            </div>
          )}
          {item.vehicle?.vin && <div className='mt-1 font-mono text-[11px] tracking-wider text-[#6B7280]'>VIN {item.vehicle.vin}</div>}

          {/* Assignee — initials only; sub-status pill stacked under the OPEN main pill for quotes (CAR-3491). */}
          <div className='mt-2 flex flex-wrap items-center gap-3' onClick={(e) => e.stopPropagation()}>
            <AssigneeAvatarPicker
              type={assigneeType}
              workOrderId={item.id}
              initialAssignee={item.assigned_to}
              closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
            />
            {itemType === 'Q' && (
              <SubStatusPicker
                quoteId={item.id}
                currentSubStatus={item.sub_status}
                closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                setBy={item.sub_status_set_by}
                setAt={item.sub_status_set_at}
              />
            )}
          </div>

          <hr className='my-3 border-[#EBEDF0]' />

          {/* Total + created — aligned with VIN/assignee */}
          <div className='flex items-end justify-between'>
            {itemType === 'Q' && LOST_SUB_STATUSES.includes(item.sub_status) ? (
              <div className='flex flex-col'>
                <div className='text-[18px] font-bold text-[#94A3B8] line-through'>
                  {currencyFormatter(item.pre_rebate_total ?? item.total_cost)}
                </div>
                <div className='text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]'>Rejected</div>
              </div>
            ) : (
              <div className='text-[18px] font-bold text-[#0A1628]'>{currencyFormatter(item.pre_rebate_total ?? item.total_cost)}</div>
            )}
            <div className='text-[12px] text-[#6B7280]'>Created {formatDate(item.created_at)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

QueueCard.propTypes = {
  item: PropTypes.object.isRequired,
  itemType: PropTypes.oneOf(['Q', 'WO', 'INV']).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  onClick: PropTypes.func,
  assigneeType: PropTypes.string
}
